import { applyConsent, CONSENT_DENIED, CONSENT_GRANTED, readStoredConsent, storeConsent } from "../lib/consent";
import { localeFromPath, pathForRoute, routeFromPath } from "../lib/router";
import { trackConversion, trackLead, trackPageView } from "../lib/tracking";
import { installSnapshot, readSnapshot } from "./page-snapshot";

type Widgets = typeof import("./widgets");
let widgets: Widgets | undefined;
let widgetsPromise: Promise<Widgets> | undefined;
const loadWidgets = () => widgetsPromise ??= import("./widgets").then((loaded) => (widgets = loaded));

export function startStaticClient(root: HTMLElement) {
  let observer: IntersectionObserver | undefined;
  let generation = 0;
  let navigation = 0;
  let uiRequest = 0;
  const normalize = (path: string) => path.replace(/\/+$/, "") || "/";
  let activePath = normalize(window.location.pathname);

  function initializePage() {
    const locale = localeFromPath(window.location.pathname);
    const stored = readStoredConsent();
    const host = root.querySelector<HTMLElement>("[data-consent-island]");
    if (stored) { applyConsent(stored); host?.replaceChildren(); }
    else {
      delete document.documentElement.dataset.consentStored;
      const banner = root.querySelector<HTMLElement>(".cc-banner");
      const token = generation;
      // Focus needs layout. Wait for the existing font/layout pass instead of
      // forcing a second whole-document layout while fonts are still loading.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        void document.fonts.ready.then(() => {
          if (token === generation && banner?.isConnected && document.activeElement === document.body) {
            banner.focus({ preventScroll: true });
          }
        });
      }));
    }
    if (routeFromPath(window.location.pathname) === "thankYou") trackConversion();
    const video = root.querySelector<HTMLElement>("[data-video-island]");
    if (video) {
      const token = generation;
      const mount = async () => {
        if (generation !== token || !video.isConnected) return;
        observer?.disconnect();
        const loaded = await loadWidgets();
        await loaded.prepareLocale(locale);
        if (generation === token && video.isConnected) loaded.mountVideo(video, locale);
      };
      if ("IntersectionObserver" in window) {
        observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) void mount(); }, { rootMargin: "200px" });
        // Observing a descendant of content-visibility:auto can force its
        // skipped contents to render. Observe the section's outer box instead.
        observer.observe(video.closest(".section-video") || video);
      } else void mount();
    }
  }

  async function settings() {
    const request = ++uiRequest;
    const token = generation;
    const locale = localeFromPath(window.location.pathname);
    const host = root.querySelector<HTMLElement>("[data-consent-island]");
    if (!host) throw new Error("Missing consent island");
    const loaded = await loadWidgets();
    await loaded.prepareLocale(locale);
    if (token === generation && request === uiRequest && host.isConnected) loaded.mountConsent(host, locale);
  }

  async function go(url: URL, fromHistory = false) {
    const token = ++navigation;
    const restoreScroll = fromHistory ? history.state?.parapendioScroll : undefined;
    try {
      const page = await readSnapshot(url.pathname);
      if (token !== navigation) return;
      if (!fromHistory) history.replaceState({
        ...history.state, parapendioScroll: [window.scrollX, window.scrollY],
      }, "", location.href);
      generation++;
      uiRequest++;
      observer?.disconnect();
      widgets?.disposeWidgets();
      installSnapshot(root, page);
      if (!fromHistory) history.pushState({}, "", url.pathname + url.search + url.hash);
      activePath = normalize(url.pathname);
      initializePage();
      const initial = normalize(window.__ROUTE__ || window.location.pathname);
      if ((page.route.startsWith("article/") && initial !== normalize(page.path)) ||
          (page.route === "thankYou" && !(window.__ROUTE__ || "").endsWith("grazie"))) {
        trackPageView(page.path, page.title);
      }
      if (Array.isArray(restoreScroll) && restoreScroll.length === 2) {
        window.scrollTo({ left: restoreScroll[0], top: restoreScroll[1], behavior: "instant" });
      } else if (url.hash) document.getElementById(decodeURIComponent(url.hash.slice(1)))?.scrollIntoView();
      else window.scrollTo(0, 0);
    } catch (error) {
      console.warn("Client navigation failed; loading the complete document", error);
      window.location.assign(url.href);
    }
  }

  document.addEventListener("click", (event) => {
    if (!(event.target instanceof Element) || event.defaultPrevented) return;
    const button = event.target.closest<HTMLElement>("button");
    // React owns these handlers once an interactive island has been mounted.
    if (button && !button.closest('[data-react-island="true"]')) {
      const policy = button.dataset.policy;
      if (policy === "privacy" || policy === "cookie") {
        const request = ++uiRequest;
        const token = generation;
        const locale = localeFromPath(window.location.pathname);
        void loadWidgets().then(async (loaded) => {
          await loaded.prepareLocale(locale);
          if (token === generation && request === uiRequest) loaded.openPolicy(policy, locale);
        });
        return;
      }
      if (button.hasAttribute("data-open-consent") || button.dataset.consentAction === "customize") {
        void settings(); return;
      }
      const action = button.dataset.consentAction;
      if (action === "accept" || action === "reject") {
        uiRequest++;
        const consent = action === "accept" ? CONSENT_GRANTED : CONSENT_DENIED;
        storeConsent(consent); applyConsent(consent);
        root.querySelector("[data-consent-island]")?.replaceChildren();
        return;
      }
      if (button.hasAttribute("data-home-link") || button.matches(".article-back")) {
        void go(new URL(pathForRoute("home", localeFromPath(location.pathname)), location.href)); return;
      }
    }
    const link = event.target.closest<HTMLAnchorElement>("a[href]");
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey ||
        event.button !== 0 || link.target || link.hasAttribute("download")) return;
    const url = new URL(link.href);
    if (url.origin !== location.origin || routeFromPath(url.pathname) === "notFound") return;
    if (normalize(url.pathname) === activePath) return; // native same-page anchors
    if (localeFromPath(url.pathname) !== localeFromPath(location.pathname)) return; // real language URL
    event.preventDefault();
    if (link.dataset.leadLabel) trackLead(link.dataset.leadLabel);
    void go(url);
  });
  window.addEventListener("popstate", () => {
    if (normalize(location.pathname) !== activePath) void go(new URL(location.href), true);
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Tab" && document.activeElement === document.body) {
      root.querySelector<HTMLElement>(".cc-banner")?.focus({ preventScroll: true });
    }
  });
  // The existing public event remains usable by callers outside the footer.
  window.addEventListener("tl:open-consent", () => { void settings(); });
  initializePage();
}
